if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const submitStartedAt = Date.now();
        const minLoadingMs = 500;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.submitButtonText) this.submitButtonText.style.opacity = '0';
        const spinner = this.submitButton.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.currentMainVariantId = String(formData.get('id') || this.variantIdInput?.value || '');
        this.resetAddedItems(formData);
        this.pendingFreeGiftItem = this.prepareFreeGiftForSubmit(formData);
        this.recordPlannedVariantIds(formData);
        if (this.cart) {
          formData.append('sections', this.getCartSectionIds().join(','));
          formData.append('sections_url', this.getCartSectionsUrl());
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const mainAddResponse = response;
            this.recordAddedItems(mainAddResponse, this.currentMainVariantId);
            const supplementalItems = this.getSupplementalItems(formData);
            const quickAddModal = this.closest('quick-add-modal');

            if (this.cart && !quickAddModal && !supplementalItems.length) {
              this.renderCartNotification(
                this.buildCartRenderState(mainAddResponse, mainAddResponse, formData)
              );
            }

            response = await this.addSupplementalItemsIfNeeded(response, formData, supplementalItems);

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const renderState = supplementalItems.length
              ? await this.getCartRenderState(mainAddResponse, response, formData, true)
              : this.buildCartRenderState(mainAddResponse, mainAddResponse, formData);

            if (!this.error) {
              const startMarker = window.CartPerformance?.createStartingMarker?.('add:wait-for-subscribers');
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                window.CartPerformance?.measureFromMarker?.('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;

            if (quickAddModal) {
              await new Promise((resolve) => {
                document.body.addEventListener('modalClosed', () => resolve(), { once: true });
                quickAddModal.hide(true);
              });
              this.renderCartNotification(renderState);
            } else if (supplementalItems.length) {
              this.renderCartNotification(renderState);
            }

            await this.waitForCartFeedback();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            const finishSubmitState = () => {
              this.submitButton =
                this.querySelector('[type="submit"]') || this.submitButton;
              this.submitButtonText =
                this.submitButton?.querySelector('span') || this.submitButtonText;
              this.submitButton?.classList.remove('loading');
              if (this.submitButtonText) this.submitButtonText.style.opacity = '';
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
              const spinnerEl =
                this.submitButton?.querySelector('.loading__spinner') ||
                this.querySelector('.loading__spinner');
              if (spinnerEl) spinnerEl.classList.add('hidden');
              window.CartPerformance?.measureFromEvent?.('add:user-action', evt);
            };

            const remainingLoadingMs = Math.max(0, minLoadingMs - (Date.now() - submitStartedAt));
            window.setTimeout(finishSubmitState, remainingLoadingMs);
          });
      }

      waitForCartFeedback() {
        const cart = this.cart;
        if (!cart) return Promise.resolve();

        // cart-drawer opens via nested setTimeout; keep ATC loading until it is active
        if (cart.matches?.('cart-drawer') || cart.tagName === 'CART-DRAWER') {
          if (cart.classList.contains('active')) return Promise.resolve();

          return new Promise((resolve) => {
            let settled = false;
            const done = () => {
              if (settled) return;
              settled = true;
              observer.disconnect();
              window.clearTimeout(timeoutId);
              resolve();
            };

            const observer = new MutationObserver(() => {
              if (cart.classList.contains('active')) done();
            });
            observer.observe(cart, { attributes: true, attributeFilter: ['class'] });

            const timeoutId = window.setTimeout(done, 1200);
          });
        }

        // cart-notification / other: wait two frames for paint
        return new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
      }

      async addSupplementalItemsIfNeeded(response, formData, supplementalItems = this.getSupplementalItems(formData)) {
        if (!supplementalItems.length) return response;

        const mainVariantId = String(formData.get('id') || this.variantIdInput?.value || '');

        try {
          // 1) 先加 Upgrade / 延保 / Bundle（不含赠品），避免外部 BMS 逻辑把 Upgrade 当成可赠品父级
          const nonGiftItems = supplementalItems.filter((item) => item?.properties?._gift !== 'true');
          const giftItems = supplementalItems.filter((item) => item?.properties?._gift === 'true');

          let latestResponse = response;

          if (nonGiftItems.length) {
            latestResponse = await this.addCartItems(nonGiftItems, { includeSections: giftItems.length === 0 });
            if (latestResponse?.status) {
              console.error('Supplemental items add failed', latestResponse);
              return response;
            }
          }

          // 2) 再单独加赠品，只绑定主商品
          if (giftItems.length) {
            const giftResponse = await this.addCartItems(giftItems, { includeSections: true });
            if (giftResponse?.status) {
              console.error('Free gift add failed', giftResponse);
            } else {
              latestResponse = giftResponse;
            }
          }

          // 3) 清掉误绑到 Upgrade 或其他父级的赠品，并去重（只保留 1 个绑主商品的赠品）
          latestResponse = await this.pruneOrphanFreeGifts(mainVariantId, latestResponse, giftItems.length > 0);

          return latestResponse;
        } catch (error) {
          console.error('Supplemental items add failed', error);
          return response;
        }
      }

      async addCartItems(items, { includeSections = true } = {}) {
        const body = { items };

        if (this.cart && includeSections) {
          body.sections = this.getCartSectionIds();
          body.sections_url = this.getCartSectionsUrl();
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';
        // GiftMore installs a global fetch interceptor. Theme-managed supplemental
        // items must bypass it or its cart reconciliation can remove this gift.
        config.headers['X-Gift-Display-Skip'] = 'true';
        config.body = JSON.stringify(body);

        const response = await fetch(`${routes.cart_add_url}`, config);
        const parsed = await response.json();
        if (!parsed?.status) {
          this.recordAddedItems(parsed, this.currentMainVariantId);
        }
        return parsed;
      }

      async pruneOrphanFreeGifts(mainVariantId, fallbackResponse, hasThemeGifts = true) {
        if (!mainVariantId || !hasThemeGifts) return fallbackResponse;

        try {
          const cart = await fetch(`${routes.cart_url}.js`, {
            credentials: 'same-origin',
            headers: { 'X-Gift-Display-Skip': 'true' },
          }).then((res) => res.json());

          if (!cart?.items?.length) return fallbackResponse;

          const upgradeVariantIds = new Set(
            cart.items
              .filter((item) => item.properties?._upgrade === 'true')
              .map((item) => String(item.variant_id))
          );

          const giftItems = cart.items.filter((item) => {
            const properties = item.properties || {};
            return (
              properties._theme_gift === 'true' ||
              (properties._gift === 'true' && properties._bms_gift !== 'true')
            );
          });

          if (!giftItems.length) return fallbackResponse;

          const updates = {};
          let keptMainGift = false;

          giftItems.forEach((item) => {
            const properties = item.properties || {};
            const parentVariantId = String(properties._bms_parent_variant_id || '');

            // 父级是 Upgrade 商品 → 一律删除
            if (parentVariantId && upgradeVariantIds.has(parentVariantId)) {
              updates[item.key] = 0;
              return;
            }

            // 其他主商品的合法赠品与本次加购无关，不能删除
            if (parentVariantId && parentVariantId !== String(mainVariantId)) {
              return;
            }

            // 无父级的旧赠品不在这里猜测归属，避免误删
            if (!parentVariantId) return;

            // 当前主商品只保留 1 个主题赠品
            if (!keptMainGift) {
              keptMainGift = true;
              return;
            }

            updates[item.key] = 0;
          });

          if (!Object.keys(updates).length) return fallbackResponse;

          const body = { updates };

          if (this.cart) {
            body.sections = this.getCartSectionIds();
            body.sections_url = this.getCartSectionsUrl();
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.headers['Content-Type'] = 'application/json';
          config.headers['X-Gift-Display-Skip'] = 'true';
          config.body = JSON.stringify(body);

          const pruned = await fetch(`${routes.cart_update_url}`, config).then((res) => res.json());
          if (pruned?.status) {
            console.error('Prune orphan free gifts failed', pruned);
            return fallbackResponse;
          }

          return pruned;
        } catch (error) {
          console.error('Prune orphan free gifts failed', error);
          return fallbackResponse;
        }
      }

      getSupplementalItems(formData) {
        const quantity = Number(formData.get('quantity')) || 1;
        const supplementalItems = [];

        // Upgrade 紧跟主产品；赠品只加一次且仅绑定主产品，与 Upgrade/Bundle 无关
        supplementalItems.push(...this.getUpgradeSaveItems(quantity));

        const protectionPlanItem = this.getProtectionPlanItem(quantity);
        if (protectionPlanItem) {
          supplementalItems.push(protectionPlanItem);
        }

        const freeGiftItem = this.pendingFreeGiftItem || this.getFreeGiftItem();
        if (freeGiftItem) {
          supplementalItems.push(freeGiftItem);
        }

        supplementalItems.push(...this.getBundleSaveItems());

        return supplementalItems;
      }

      getSupplementalScope() {
        return this.closest('product-info') || this.closest('.product__info-container') || document;
      }

      getProtectionPlanItem(quantity) {
        const scope = this.getSupplementalScope();
        if (!scope) return null;

        const activeOption = scope.querySelector('.main-product-protection-plan [data-protection-option].is-active');
        if (!activeOption?.dataset.variantId) return null;

        return {
          id: parseInt(activeOption.dataset.variantId, 10),
          quantity,
          properties: {
            _protection_plan: 'true',
            _protection_parent_variant_id: String(this.variantIdInput?.value || ''),
          },
        };
      }

      getFreeGiftItem() {
        const scope = this.getSupplementalScope();
        if (!scope) return null;

        // 只取主商品信息区内的赠品模块，避免和 Upgrade 等加购模块混淆
        const giftWrapper = scope.querySelector('[data-free-gift]');
        if (!giftWrapper) return null;

        const variantSelect = giftWrapper.querySelector('[data-free-gift-variant-select]');
        const variantInput = giftWrapper.querySelector('[data-free-gift-variant-input]');
        const giftVariantId = parseInt(variantSelect?.value || variantInput?.value || '', 10);
        if (!giftVariantId) return null;

        const titleInput = giftWrapper.querySelector('[data-free-gift-title-input]');
        const productInfo = this.closest('product-info');
        // 父级必须是主商品 variant，不能是 Upgrade / Bundle 的 variant
        const parentVariantId = this.variantIdInput?.value;
        const parentProductId = productInfo?.dataset?.productId;

        if (!parentVariantId) return null;

        const properties = {
          _gift: 'true',
          _theme_gift: 'true',
          'Free Gift': titleInput?.value || 'Free Gift',
          _bms_parent_variant_id: String(parentVariantId),
        };

        if (parentProductId) {
          properties._gift_source = String(parentProductId);
        }

        return {
          id: giftVariantId,
          quantity: 1,
          properties,
        };
      }

      prepareFreeGiftForSubmit(formData) {
        const giftItem = this.getFreeGiftItem();
        if (!giftItem) return null;

        const productInfo = this.closest('product-info');
        const parentProductId = String(productInfo?.dataset?.productId || '');
        const groupId =
          window.crypto?.randomUUID?.() ||
          `theme-gift-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const giftVariantId = Number(giftItem.id);
        const selectedGifts = [{ variant_id: giftVariantId, quantity: 1 }];

        giftItem.properties._bms_gift = 'true';
        giftItem.properties._bms_gift_group_id = groupId;

        formData.set(
          'properties[_selected_gift]',
          JSON.stringify([`gid://shopify/ProductVariant/${giftVariantId}`])
        );
        formData.set('properties[_bms_selected_gifts]', JSON.stringify(selectedGifts));
        formData.set('properties[_bms_gift_source_product_id]', parentProductId);
        formData.set('properties[_bms_gift_lines_added]', 'true');
        formData.set('properties[_bms_gift_group_id]', groupId);

        return giftItem;
      }

      getUpgradeSaveItems(quantity) {
        const scope = this.getSupplementalScope();
        if (!scope) return [];

        const productInfo = this.closest('product-info');
        const parentVariantId = this.variantIdInput?.value;
        const parentProductId = productInfo?.dataset?.productId;

        return Array.from(scope.querySelectorAll('.main-product-upgrade-save [data-upgrade-item]')).reduce(
          (items, item) => {
            const checkbox = item.querySelector('[data-upgrade-checkbox]');
            const select = item.querySelector('[data-upgrade-variant-select]');

            if (!checkbox?.checked || !select?.value) return items;

            // 绝不使用 _bms_* / _gift_* 字段，避免外部赠品逻辑把 Upgrade 当成可赠品父级
            const properties = {
              _upgrade: 'true',
            };

            if (parentVariantId) {
              properties._upgrade_parent_variant_id = String(parentVariantId);
            }

            if (parentProductId) {
              properties._upgrade_parent_product_id = String(parentProductId);
            }

            items.push({
              id: parseInt(select.value, 10),
              quantity,
              properties,
            });

            return items;
          },
          []
        );
      }


      getBundleSaveItems() {
        const scope = this.getSupplementalScope();
        if (!scope) return [];

        const parentVariantId = this.variantIdInput?.value;

        return Array.from(scope.querySelectorAll('.main-product-bundle-save.is-selected')).reduce(
          (items, bundle) => {
            const item1Select = bundle.querySelector('[data-bundle-variant="item-1"]');
            const item2Select = bundle.querySelector('[data-bundle-variant="item-2"]');
            const item1VariantId = item1Select?.value || bundle.dataset.item1VariantId;
            const item2VariantId = item2Select?.value || bundle.dataset.item2VariantId;
            // 不与主商品数量相乘：左侧固定 1；右侧取角标数量（未设置则为 1）
            const item2Qty = Math.max(1, parseInt(bundle.dataset.item2Quantity, 10) || 1);
            const properties = {
              _bundle_save: 'true',
            };

            if (parentVariantId) {
              properties._bundle_parent_variant_id = String(parentVariantId);
            }

            if (item1VariantId) {
              items.push({
                id: parseInt(item1VariantId, 10),
                quantity: 1,
                properties: { ...properties },
              });
            }

            if (item2VariantId) {
              items.push({
                id: parseInt(item2VariantId, 10),
                quantity: item2Qty,
                properties: { ...properties },
              });
            }

            return items;
          },
          []
        );
      }

      renderCartNotification(renderState) {
        if (!this.cart || !renderState) return;

        try {
          this.cart.renderContents(renderState);
        } catch (error) {
          console.error(error);
          this.cart.open?.();
        }
      }

      getCartSectionIds() {
        return (
          this.cart
            ?.getSectionsToRender?.()
            .map((section) => section.id)
            .filter(Boolean) || []
        );
      }

      getCartSectionsUrl() {
        return window.routes?.cart_url || '/cart';
      }

      async getCartRenderState(mainResponse, latestResponse, formData, afterSupplementals = false) {
        const latest = latestResponse || mainResponse || {};
        let sections = latest.sections;

        if (afterSupplementals) {
          if (latest === mainResponse || !sections?.['cart-notification-product']) {
            sections = (await this.fetchCartSections()) || sections || mainResponse?.sections;
          }
        } else if (!sections?.['cart-notification-product']) {
          sections = (await this.fetchCartSections()) || sections;
        }

        return this.buildCartRenderState(mainResponse, latest, formData, sections);
      }

      buildCartRenderState(mainResponse, latestResponse, formData, sections) {
        const latest = latestResponse || mainResponse || {};
        const mainVariantId = String(formData.get('id') || this.variantIdInput?.value || '');
        const productInfo = this.closest('product-info');
        const mainProductId = String(productInfo?.dataset?.productId || '');

        return {
          ...latest,
          key: this.getAddedLineItemKey(mainResponse, latest, mainVariantId),
          id: Number(mainVariantId) || mainResponse?.id || latest.id,
          variant_id: Number(mainVariantId) || mainResponse?.variant_id || latest.variant_id,
          product_id: mainProductId,
          addedItemKeys: Array.from(this.addedItemKeys || []),
          addedVariantIds: Array.from(this.addedVariantIds || []),
          sections: sections || latest.sections || mainResponse?.sections,
        };
      }

      resetAddedItems(formData) {
        this.addedItemKeys = new Set();
        this.addedVariantIds = new Set();
        this.recordPlannedVariantIds(formData);
      }

      recordPlannedVariantIds(formData) {
        if (!this.addedVariantIds) this.addedVariantIds = new Set();
        const mainVariantId = String(formData?.get('id') || this.variantIdInput?.value || '');
        if (mainVariantId) this.addedVariantIds.add(mainVariantId);
        this.getSupplementalItems(formData).forEach((item) => {
          if (item?.id) this.addedVariantIds.add(String(item.id));
        });
      }

      recordAddedItems(payload, mainVariantId) {
        if (!payload || payload.status) return;
        if (!this.addedItemKeys) this.addedItemKeys = new Set();
        if (!this.addedVariantIds) this.addedVariantIds = new Set();

        const isFullCart = Boolean(payload.token || typeof payload.item_count === 'number');

        const recordItem = (item) => {
          if (!item?.key) return;
          const variantId = String(item.variant_id || item.id || '');

          if (!isFullCart) {
            this.addedItemKeys.add(String(item.key));
            if (variantId) this.addedVariantIds.add(variantId);
            return;
          }

          if (!variantId || !this.addedVariantIds.has(variantId)) return;
          if (this.isAddonCartItem(item) && mainVariantId && !this.isAddonForParent(item, mainVariantId)) return;
          this.addedItemKeys.add(String(item.key));
        };

        if (Array.isArray(payload.items)) {
          payload.items.forEach(recordItem);
          return;
        }

        recordItem(payload);
      }

      isAddonForParent(item, mainVariantId) {
        if (!this.isAddonCartItem(item)) return false;
        const properties = item.properties || {};
        const parentVariantId = String(
          properties._upgrade_parent_variant_id ||
            properties._bms_parent_variant_id ||
            properties._bundle_parent_variant_id ||
            properties._protection_parent_variant_id ||
            ''
        );
        return parentVariantId === String(mainVariantId);
      }

      getAddedLineItemKey(mainResponse, latestResponse, mainVariantId) {
        if (mainResponse?.key && !Array.isArray(mainResponse.items)) {
          return mainResponse.key;
        }

        const recordedKeys = Array.from(this.addedItemKeys || []);
        if (recordedKeys.length) return recordedKeys[0];

        const items = [];
        const collectFromAddPayload = (payload) => {
          if (!payload) return;
          if (payload.token || typeof payload.item_count === 'number') return;
          if (Array.isArray(payload.items)) items.push(...payload.items);
          else if (payload.key) items.push(payload);
        };
        collectFromAddPayload(mainResponse);
        collectFromAddPayload(latestResponse);

        const match = items.find(
          (item) => String(item.variant_id || item.id) === String(mainVariantId) && !this.isAddonCartItem(item)
        );
        return match?.key || '';
      }

      isAddonCartItem(item) {
        const properties = item?.properties || {};
        return (
          properties._upgrade === 'true' ||
          properties._gift === 'true' ||
          properties._bms_gift === 'true' ||
          properties._theme_gift === 'true' ||
          properties._protection_plan === 'true' ||
          properties._bundle_save === 'true'
        );
      }

      async fetchCartSections() {
        const sectionIds = this.getCartSectionIds();
        if (!sectionIds.length) return null;

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.headers['Content-Type'] = 'application/json';
          config.headers['X-Gift-Display-Skip'] = 'true';
          config.body = JSON.stringify({
            updates: {},
            sections: sectionIds,
            sections_url: this.getCartSectionsUrl(),
          });

          const response = await fetch(`${routes.cart_update_url}`, config);
          if (!response.ok) return null;
          const parsed = await response.json();
          return parsed?.sections || null;
        } catch (error) {
          console.error('Fetch cart notification sections failed', error);
          return null;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}